/**
 * ProviderPicker — generic collapsible provider/model selector.
 *
 * API surface:
 *   <ProviderPicker
 *     options={PickerOption[]}        // full list (already translated labels)
 *     value={string | null}           // currently selected value
 *     onChange={(v) => void}          // called on selection
 *     label="AI Provider"             // section header
 *     defaultExpanded={false}         // collapsed by default
 *     sortKeys={['price','speed','quality','name']}
 *     defaultSortKey="price"
 *     searchable                      // auto-true when options.length >= 10
 *     extraFilters={<FreeOnlyToggle />}
 *     renderCardExtras={(opt) => <ModelStatsPanel ... />}
 *     groupBy={(opt) => opt.meta.group ?? null}
 *     emptyState={<p>No results</p>}
 *     disabled
 *   />
 *
 * Collapsed state: shows the recommended option (or current selection) with a
 * preview row; click anywhere to expand.
 * Expanded state: toolbar (search + sort + extraFilters) + responsive card grid.
 *
 * Mobile (360px): grid-cols-1, toolbar items scroll horizontally, search full-width,
 * all tap targets >= 44px.
 */

import { useState, useMemo, useRef, useEffect, useCallback, memo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useClickOutside } from '@/hooks/useClickOutside';
import { ProviderCard } from './ProviderCard';
import { ProviderToolbar } from './ProviderToolbar';
import { sortOptions, filterBySearch, groupOptions } from './helpers';
import type { PickerOption, ProviderPickerProps, SortKey } from './types';

// =============================================================================
// COLLAPSED PREVIEW ROW
// =============================================================================

function CollapsedRow<TMeta>({
  selected,
  recommended,
  disabled,
  onExpand,
}: {
  selected: PickerOption<TMeta> | undefined;
  recommended: PickerOption<TMeta> | undefined;
  disabled: boolean;
  onExpand: () => void;
}) {
  const { t } = useTranslation();
  const preview = selected ?? recommended;
  const isRecommended = preview?.recommended === true && preview?.value === selected?.value;

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onExpand}
      disabled={disabled}
      aria-expanded={false}
      aria-haspopup="listbox"
      className={[
        'w-full flex items-center gap-3 p-3 min-h-[44px]',
        'border border-white/10 rounded-xl bg-black/30',
        'hover:border-white/20 hover:bg-black/40 transition-all',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
      ]
        .join(' ')
        .trim()}
    >
      {/* Icon */}
      {preview?.iconUrl && (
        <img
          src={preview.iconUrl}
          alt=""
          aria-hidden="true"
          className="w-8 h-8 rounded-md object-cover shrink-0"
        />
      )}

      {/* Label + subtitle */}
      <div className="flex-1 text-left min-w-0">
        {preview ? (
          <>
            <span className="block text-sm font-semibold text-white truncate">
              {preview.label}
              {isRecommended && (
                <span className="ml-1.5 text-xs font-normal text-purple-400">
                  ({t('providerPicker.recommended', 'Recommended')})
                </span>
              )}
            </span>
            {/* One-line summary: pricing · speed · quality */}
            <span className="block text-xs text-white/40 truncate">
              {[
                preview.pricing,
                preview.speed,
                preview.quality !== undefined ? `${'★'.repeat(preview.quality)}` : undefined,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </>
        ) : (
          <span className="text-sm text-white/40">
            {t('providerPicker.selectPrompt', 'Select...')}
          </span>
        )}
      </div>

      {!selected && recommended && (
        <span className="text-xs text-white/30 shrink-0 hidden sm:block">
          {t('providerPicker.change', 'Change')}
        </span>
      )}

      <ChevronDown className="h-4 w-4 text-white/40 shrink-0" />
    </button>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * True when a mousedown landed on an element's native scrollbar gutter rather
 * than on its content. Clicking a scrollbar reports the scrollable element
 * itself as the event target, and clientWidth/clientHeight exclude the
 * scrollbar — so a click at x >= contentRight (or y >= contentBottom) is on the
 * gutter. Used to keep the picker open while the user drags the page/tab
 * scrollbar to reach models further down the list.
 */
function isScrollbarClick(event: MouseEvent): boolean {
  const el = event.target as HTMLElement | null;
  if (!el || el.nodeType !== 1) return false;
  const hasVScroll = el.scrollHeight > el.clientHeight;
  const hasHScroll = el.scrollWidth > el.clientWidth;
  if (!hasVScroll && !hasHScroll) return false;
  const rect = el.getBoundingClientRect();
  const onVScrollbar = hasVScroll && event.clientX >= rect.left + el.clientWidth;
  const onHScrollbar = hasHScroll && event.clientY >= rect.top + el.clientHeight;
  return onVScrollbar || onHScrollbar;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function ProviderPickerInner<TMeta = unknown>({
  options,
  value,
  onChange,
  label,
  defaultExpanded = false,
  sortKeys = ['price', 'speed', 'quality', 'name'],
  defaultSortKey = 'price',
  searchable,
  extraFilters,
  renderCardExtras,
  renderCard,
  groupBy,
  emptyState,
  className = '',
  disabled = false,
  onDisabledClick,
  cardMinWidth = 140,
}: ProviderPickerProps<TMeta>) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>(defaultSortKey);
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const isSearchable = searchable !== undefined ? searchable : options.length >= 10;

  // Close on click outside — but ignore clicks that land on a scrollbar gutter.
  // Clicking the page/tab scroll container's scrollbar reports that (outside)
  // element as the target; without this guard, dragging the scrollbar to reach
  // models lower in the list would dismiss the picker. Wheel scrolling never
  // fires mousedown, so it was always fine.
  useClickOutside(containerRef, () => setIsExpanded(false), {
    enabled: isExpanded,
    ignore: isScrollbarClick,
  });

  // Clear search on collapse
  useEffect(() => {
    if (!isExpanded) setSearchQuery('');
  }, [isExpanded]);

  // When expanded, pull the panel into view. If the collapsed row sat low on the
  // page, the panel (toolbar + tall card grid) overflows below the fold and the
  // page doesn't follow — leaving the model grid mostly off-screen. Scrolling it
  // in makes the internal 60vh scroll area usable without scrolling the page.
  useEffect(() => {
    if (!isExpanded) return;
    const panel = panelRef.current;
    if (!panel) return;
    const raf = requestAnimationFrame(() => {
      const rect = panel.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom <= window.innerHeight) return; // already fully visible
      panel.scrollIntoView({
        behavior: 'smooth',
        // Align to the top when the panel is taller than the viewport so the
        // toolbar stays visible; otherwise a minimal scroll so the page follows.
        block: rect.height > window.innerHeight ? 'start' : 'nearest',
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isExpanded]);

  // Derived data
  const recommended = useMemo(() => options.find((o) => o.recommended), [options]);
  const selected = useMemo(
    () => (value !== null ? options.find((o) => o.value === value) : undefined),
    [options, value]
  );

  const processedOptions = useMemo(() => {
    const filtered = filterBySearch(options, searchQuery);
    return sortOptions(filtered, sortKey);
  }, [options, searchQuery, sortKey]);

  const grouped = useMemo<Record<string, PickerOption<TMeta>[]>>(() => {
    if (!groupBy) return { _all: processedOptions };
    return groupOptions(processedOptions, groupBy);
  }, [processedOptions, groupBy]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      setIsExpanded(false);
    },
    [onChange]
  );

  return (
    <div className={`space-y-2 ${className}`} ref={containerRef}>
      {/* Section header */}
      {label && (
        <label className="form-label flex items-center gap-2 text-sm font-medium text-white/70">
          {label}
        </label>
      )}

      {/* Collapsed state */}
      {!isExpanded && (
        <CollapsedRow
          selected={selected}
          recommended={recommended}
          disabled={disabled}
          onExpand={() => setIsExpanded(true)}
        />
      )}

      {/* Expanded state */}
      {isExpanded && (
        <div
          ref={panelRef}
          className="border border-white/10 rounded-xl bg-black/40 backdrop-blur-xl shadow-lg animate-in slide-in-from-top-2 duration-200"
        >
          {/* Expanded header */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-white/5">
            <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              {label ?? t('providerPicker.selectPrompt', 'Select')}
            </span>
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label={t('providerPicker.collapse', 'Collapse')}
            >
              <ChevronUp className="h-4 w-4" />
            </button>
          </div>

          {/* Toolbar */}
          <div className="px-4 py-3">
            <ProviderToolbar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortKey={sortKey}
              onSortChange={setSortKey}
              sortKeys={sortKeys}
              extraFilters={extraFilters}
              resultCount={processedOptions.length}
              searchable={isSearchable}
            />
          </div>

          {/* Card grid */}
          <div className="px-4 pb-4 space-y-4 max-h-[60vh] overflow-y-auto scroll-smooth-touch">
            {Object.entries(grouped).map(([groupKey, groupItems]) => (
              <div key={groupKey}>
                {/* Group header — only when groupBy is active and key is not synthetic */}
                {groupBy && groupKey !== '_all' && groupKey !== '_ungrouped' && (
                  <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">
                    {groupKey}
                    <span className="ml-1.5 font-normal normal-case">({groupItems.length})</span>
                  </h4>
                )}

                <div
                  className="grid gap-1.5 auto-rows-fr"
                  style={{
                    gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${cardMinWidth}px), 1fr))`,
                  }}
                >
                  {groupItems.map((opt) =>
                    renderCard ? (
                      <div key={opt.value}>
                        {renderCard(opt, opt.value === value, () => handleSelect(opt.value))}
                      </div>
                    ) : (
                      <ProviderCard<TMeta>
                        key={opt.value}
                        option={opt}
                        isSelected={opt.value === value}
                        onClick={() => handleSelect(opt.value)}
                        disabled={disabled}
                        onDisabledClick={onDisabledClick}
                        renderExtras={renderCardExtras}
                      />
                    )
                  )}
                </div>
              </div>
            ))}

            {/* Empty state */}
            {processedOptions.length === 0 &&
              (emptyState ?? (
                <p className="text-center text-white/40 py-8 text-sm">
                  {t('providerPicker.noResults', 'No results found')}
                </p>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// memo() with generic doesn't work directly — wrap with explicit cast
export const ProviderPicker = memo(ProviderPickerInner) as typeof ProviderPickerInner;

export default ProviderPicker;

// Re-export types for consumers who import from this file directly
export type { PickerOption, ProviderPickerProps, SortKey } from './types';
